import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import { FormDialog, Field, InputGroup } from 'core/form';
import { Box, Text } from 'core/components';
import { showSuccessToast } from 'core/components/toast';
import $boards from 'app/stores/boards/$boards';
import BoardPanelModel from 'app/stores/boards/BoardPanelModel';
import BoardSelector from './BoardSelector';

interface AddToBoardDialogProps {
  isOpen: boolean;
  panel: BoardPanelModel;
  defaultBoardId?: string;
  onClose: () => void;
}

const fields = {
  panel_title: { label: 'Title', rules: 'required' },
  board_id: { label: 'Board', rules: 'required' }
};

const options = {
  name: 'Add to Existing Board',
  showPristineErrors: false
};

const AddToBoardDialog = observer(({ isOpen, panel, defaultBoardId, onClose }: AddToBoardDialogProps) => {
  const initialValues = {
    panel_title: panel?.title || '',
    board_id: defaultBoardId || ''
  };

  const handleSubmit = useCallback(
    async (_form: unknown, values: { panel_title: string; board_id: string }) => {
      const targetBoardId = Number(values.board_id);
      if (!Number.isFinite(targetBoardId) || targetBoardId <= 0) {
        throw new Error('Invalid board selection');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const targetBoard: any = $boards.collection.get(targetBoardId) || $boards.collection.forge({ id: targetBoardId });

      if (!targetBoard.panelCollection.size) {
        await targetBoard.fetch();
      }

      await targetBoard.addPanel({
        type: 'ude',
        title: values.panel_title,
        config: panel.get('config'),
        parent_panel_id: null
      });

      const boardName = targetBoard.name;
      showSuccessToast(
        <span>
          Panel added to <a href={targetBoard.navigatePath}>{boardName}</a>
        </span>,
        { timeout: 8000 }
      );

      onClose();
    },
    [panel, onClose]
  );

  return (
    <FormDialog
      isOpen={isOpen}
      title="Add to Existing Board"
      entityName="Panel"
      fields={fields}
      options={options}
      width={500}
      onClose={onClose}
      formActionsProps={{ showRemove: false, onSubmit: handleSubmit, onCancel: onClose }}
      formComponentProps={{ values: initialValues }}
    >
      <Box p={2}>
        <Field name="panel_title" label="Title" mb={2}>
          <InputGroup fill />
        </Field>
        <Text fontWeight="bold" mb="4px">
          Board
        </Text>
        <BoardSelector name="board_id" showLabel={false} fill mb={0} rules="required" />
      </Box>
    </FormDialog>
  );
});

export default AddToBoardDialog;
